import { refreshAccessToken, validAccessToken } from '@/lib/auth/session';
import { networkError, toApiError } from './errors';

export const API_BASE = '/api';

type Query = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  /** Required by finance writes; reuse the same key when retrying the same action. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Send the access token (default true). */
  auth?: boolean;
}

export function buildUrl(path: string, query?: Query): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * fetch wrapper: bearer token, JSON in and out, typed ApiError on failure,
 * and one transparent retry after refreshing an expired access token.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, idempotencyKey, signal, auth = true } = opts;
  const url = buildUrl(path, query);

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
    try {
      return await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), signal });
    } catch (e) {
      if (signal?.aborted) throw e;
      throw networkError(e);
    }
  };

  const token = auth ? await validAccessToken() : null;
  let res = await send(token);

  if (res.status === 401 && auth) {
    const error = toApiError(401, await readBody(res));
    // Only an expired/invalid access token is worth a refresh; wrong passwords etc. are final.
    if (error.code !== 'UNAUTHENTICATED') throw error;
    res = await send(await refreshAccessToken());
  }

  const data = await readBody(res);
  if (!res.ok) throw toApiError(res.status, data);
  return data as T;
}

export const api = {
  get: <T>(path: string, query?: Query, opts?: Omit<RequestOptions, 'query' | 'method' | 'body'>) =>
    request<T>(path, { ...opts, query }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body: body ?? {} }),
  delete: <T = void>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) => request<T>(path, { ...opts, method: 'DELETE' }),
};
